import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react'
import { mappedColors } from '@pixee/design-system'
import classNames from 'classnames'
import { useEffect, useRef, useState } from 'react'
import * as styles from './scrollable-tab-bar.css.ts'

export type ScrollableTabBarProps = React.PropsWithChildren<{
  trailingContent?: React.ReactNode
  className?: string
}>

export const ScrollableTabBar: React.FC<ScrollableTabBarProps> = ({ children, trailingContent, className }) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [isScrollable, setIsScrollable] = useState(false)
  const [isLeftScrollIndicatorVisible, setIsLeftScrollIndicatorVisible] = useState(false)
  const [isRightScrollIndicatorVisible, setIsRightScrollIndicatorVisible] = useState(false)

  useEffect(() => {
    const el = scrollRef.current
    if (!el) {
      return
    }

    const updateScrollIndicators = () => {
      // tolerance: getBoundingClientRect / scroll offsets are sub-pixel; a 1px tolerance avoids
      // false positives on fractional widths.
      setIsScrollable(el.scrollWidth > el.clientWidth + 1)
      setIsLeftScrollIndicatorVisible(el.scrollLeft > 0)
      setIsRightScrollIndicatorVisible(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
    }

    const updatePartiallyHiddenTabs = () => {
      const containerRect = el.getBoundingClientRect()
      const tabs = el.querySelectorAll<HTMLElement>('[role="tab"]')
      const tolerance = 1
      for (const tab of tabs) {
        const tabRect = tab.getBoundingClientRect()
        const isFullyVisible =
          tabRect.left >= containerRect.left - tolerance && tabRect.right <= containerRect.right + tolerance
        tab.dataset.partiallyHidden = isFullyVisible ? 'false' : 'true'
      }
    }

    const onScrollOrResize = () => {
      updateScrollIndicators()
      updatePartiallyHiddenTabs()
    }

    const onTabClick = (e: MouseEvent) => {
      const target = e.target
      if (!(target instanceof Element)) {
        return
      }
      const tab = target.closest<HTMLElement>('[role="tab"]')
      if (tab?.dataset.partiallyHidden !== 'true') {
        return
      }
      tab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
    }

    updateScrollIndicators()
    updatePartiallyHiddenTabs()
    el.addEventListener('scroll', onScrollOrResize)
    el.addEventListener('click', onTabClick)

    const resizeObserver = new ResizeObserver(onScrollOrResize)
    resizeObserver.observe(el)

    const rafId = requestAnimationFrame(() => updatePartiallyHiddenTabs())

    return () => {
      cancelAnimationFrame(rafId)
      el.removeEventListener('scroll', onScrollOrResize)
      el.removeEventListener('click', onTabClick)
      resizeObserver.disconnect()
    }
  }, [children])

  const scrollPrev = () => {
    const el = scrollRef.current
    if (!el) {
      return
    }
    const containerLeft = el.getBoundingClientRect().left
    const tabs = [...el.querySelectorAll<HTMLElement>('[role="tab"]')].reverse()
    const target = tabs.find(tab => tab.getBoundingClientRect().left < containerLeft - 1)
    target?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
  }

  const scrollNext = () => {
    const el = scrollRef.current
    if (!el) {
      return
    }
    const containerRight = el.getBoundingClientRect().right
    const tabs = el.querySelectorAll<HTMLElement>('[role="tab"]')
    const target = [...tabs].find(tab => tab.getBoundingClientRect().right > containerRight + 1)
    target?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
  }

  return (
    <div className={classNames(styles.row, className)}>
      {isScrollable && (
        <button
          type="button"
          aria-label="Scroll tabs left"
          aria-hidden={!isLeftScrollIndicatorVisible}
          data-hidden={!isLeftScrollIndicatorVisible}
          onClick={scrollPrev}
          className={styles.chevron}
        >
          <CaretLeftIcon size={20} weight="light" style={{ color: mappedColors.primary.pulse }} />
        </button>
      )}
      <div ref={scrollRef} className={styles.scrollArea}>
        {children}
      </div>
      {isScrollable && (
        <button
          type="button"
          aria-label="Scroll tabs right"
          aria-hidden={!isRightScrollIndicatorVisible}
          data-hidden={!isRightScrollIndicatorVisible}
          onClick={scrollNext}
          className={styles.chevron}
        >
          <CaretRightIcon size={20} weight="light" style={{ color: mappedColors.primary.pulse }} />
        </button>
      )}
      {trailingContent && <div className={styles.trailing}>{trailingContent}</div>}
    </div>
  )
}
